import PropTypes from 'prop-types';
import React from 'react';
import compact from 'lodash/compact';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';

import {
  libraryShape,
  bibShape,
  catalogBibShape,
  listShape,
  promoShape,
  recommendationShape,
  routerShape
} from '@bibliocommons/bc-prop-types';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';
import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from '@bibliocommons/utils-routing';
import AnalyticsConstants from '@bibliocommons/constants-analytics';

import Context from '@bibliocommons/context';

import { hasPromosEnabled } from 'app/helpers/library/features';
import { searchCards as searchCardsAction } from 'app/actions/CardsActions';
import ErrorPage from 'app/components/ErrorPage';
import {
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TITLE,
  UI_CONTAINER_TYPE,
  METADATA_CATEGORY_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { selectEventCards, selectCardsForSearch, selectCardSearchParameters } from 'app/selectors/CardsSelector';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectManifestationsForCurrentBib } from 'app/selectors/catalogBibs/ManifestationsSelector';
import { selectLinkedDataForCurrentBib } from 'app/selectors/bibs/LinkedDataSelector';
import { selectAnalyticsContextForCurrentBib } from 'app/selectors/bibs/AnalyticsSelector';
import { selectTagSummariesForCurrentBib } from 'app/selectors/bibs/UgcSelector';
import {
  selectStaffListsForCurrentBib,
  selectRecommendationsForCurrentBib,
  selectRelatedTitlesForCurrentBib,
  selectAuthorSuggestionsForCurrentBib
} from 'app/selectors/bibs/DiscoverySelector';
import {
  selectRelatedAuthorsForCurrentBib,
  selectOnlineResourcesForCurrentBib
} from 'app/selectors/bibs/EvaluationSelector';
import mapPromoRecommendationTypeToProperty from 'app/helpers/analytics/mapPromoRecommendationTypeToProperty';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectPromoEntities } from 'app/selectors/EntitiesSelector';
import cardShape from 'app/shapes/cardShape';
import { bibContextShape } from 'app/shapes/analytics';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { AnalyticsContextContainer } from 'app/components/analytics/AnalyticsContext';

import {
  trackCatalogExplore,
  trackContentClick as trackContentClickAction,
  trackContentExplore as trackContentExploreAction,
  trackContentImpression as trackContentImpressionAction
} from 'app/actions/AnalyticsGa4Actions';
import { authorSuggestionShape, onlineResourceShape, relatedAuthorShape, tagShape } from 'app/shapes/bibPage';
import { linkedDataShape } from 'app/shapes/document';
import BibPage from './BibPage';

export class BibPageContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  componentDidMount() {
    const { features } = this.context;
    const { catalogBib, currentLibrary, manifestations, searchCards } = this.props;

    const hasPromosOnContentPages =
      features.get(TOGGLZ_FEATURES.CORE_V2_PROMOS_ON_CONTENT_PAGES) && hasPromosEnabled(currentLibrary);
    if (!hasPromosOnContentPages) {
      return;
    }

    const limit = 4;
    const metadataIds = [catalogBib?.get('id')].concat(
      manifestations.map(manifestation => manifestation.get('id'))?.toJS()
    );
    const postTypes = ['bccms_news', 'bw_event', 'bw_custom_card', 'bw_or_card', 'post'];
    const recommendationTypes = ['contextual'];

    searchCards({
      limit,
      metadataIds,
      postTypes,
      recommendationTypes
    });
  }

  handleRelatedTitleClick(relatedTitle) {
    const bibId = relatedTitle.get('metadataId');
    const metadata = {
      metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
      metadata_value: relatedTitle.getIn(['brief', 'title'])
    };
    const ui = {
      ui_container_type: 'section',
      ui_container_title: 'related-titles',
      ui_component_type: 'link-image'
    };

    this.props.trackContentClick({ bibId, metadata, ui });
  }

  trackEventCardImpression = ({ card, position }) => {
    const { cardSearchParameters, promosById, trackContentImpression } = this.props;

    const cardId = card.get('id');
    const cardTitle = card.getIn(['title', 'text']);
    const cardType = card.get('cardType');

    const impressionId = `${cardId}-${cardSearchParameters.get('requestId')}`;

    const promo = promosById.get(String(cardId));
    const recommendationType = promo?.get('recommendationType');
    const promotionType = mapPromoRecommendationTypeToProperty(recommendationType);

    trackContentImpression({
      card: {
        card_id: cardId,
        card_title: cardTitle,
        card_type: cardType
      },
      properties: {
        impression_id: impressionId,
        promotion_type: promotionType
      },
      ui: {
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS_UPCOMING_EVENTS,
        ui_container_type: UI_CONTAINER_TYPE.CARD
      }
    });
  };

  trackEventCardLocationClick = ({ card, linkText, position }) => {
    const { cardSearchParameters, promosById, trackContentExplore } = this.props;

    const cardId = card.get('id');
    const cardTitle = card.getIn(['title', 'text']);
    const cardType = card.get('cardType');

    const impressionId = `${cardId}-${cardSearchParameters.get('requestId')}`;

    const promo = promosById.get(String(cardId));
    const recommendationType = promo?.get('recommendationType');
    const promotionType = mapPromoRecommendationTypeToProperty(recommendationType);

    trackContentExplore({
      card: {
        card_id: cardId,
        card_title: cardTitle,
        card_type: cardType
      },
      properties: {
        impression_id: impressionId,
        promotion_type: promotionType
      },
      ui: {
        ui_component_label: linkText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS_UPCOMING_EVENTS,
        ui_container_type: UI_CONTAINER_TYPE.CARD
      }
    });
  };

  trackEventCardTitleClick = ({ card, position }) => {
    const { cardSearchParameters, promosById, trackContentClick } = this.props;

    const cardId = card.get('id');
    const cardTitle = card.getIn(['title', 'text']);
    const cardType = card.get('cardType');

    const impressionId = `${cardId}-${cardSearchParameters.get('requestId')}`;

    const promo = promosById.get(String(cardId));
    const recommendationType = promo?.get('recommendationType');
    const promotionType = mapPromoRecommendationTypeToProperty(recommendationType);

    trackContentClick({
      card: {
        card_id: cardId,
        card_title: cardTitle,
        card_type: cardType
      },
      properties: {
        impression_id: impressionId,
        promotion_type: promotionType
      },
      ui: {
        ui_component_label: cardTitle,
        ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS_UPCOMING_EVENTS,
        ui_container_type: UI_CONTAINER_TYPE.CARD
      }
    });
  };

  renderDocumentHead() {
    const { catalogBib, linkedData } = this.props;

    const pageTitle = catalogBib.getIn(['brief', 'title']);
    const imageUrl = catalogBib.getIn(['brief', 'coverImage', 'large']);
    const authorName = catalogBib.getIn(['brief', 'creators', 0, 'fullName']);
    const description = compact([pageTitle, authorName, catalogBib.getIn(['brief', 'description'])]).join(' — ');
    const pageUrl = `${this.context.baseURL}/v2/record/${catalogBib.get('metadataId')}`;
    const noIndex = catalogBib.get('localizationLevel') === 'UNMATCHED';

    return (
      <DocumentHeadContainer
        pageTitle={pageTitle}
        description={description}
        imageUrl={imageUrl}
        linkedData={linkedData}
        pageUrl={pageUrl}
        noIndex={noIndex}
      />
    );
  }

  render() {
    const {
      catalogBib,
      currentLibrary,
      eventCards,
      promoCards,
      staffLists,
      recommendations,
      tags,
      relatedTitles,
      authorSuggestions,
      relatedAuthors,
      analyticsContext,
      onlineResources,
      trackCatalogExplore: trackCatalogExploreAction
    } = this.props;

    if (catalogBib) {
      const displayProps = {
        staffLists,
        recommendations,
        relatedTitles,
        tags,
        authorSuggestions,
        relatedAuthors,
        onlineResources
      };

      const showResearchAbout = currentLibrary.get('showResearchAbout', false);

      return (
        <div>
          {this.renderDocumentHead()}
          <AnalyticsContextContainer context={analyticsContext} contextName={AnalyticsConstants.CONTEXTS.BIB} />
          <BibPage
            catalogBib={this.props.catalogBib}
            eventCards={eventCards}
            promoCards={promoCards}
            {...displayProps}
            handleRelatedTitleClick={this.handleRelatedTitleClick}
            showResearchAbout={showResearchAbout}
            trackCatalogExplore={trackCatalogExploreAction}
            trackEventCardImpression={this.trackEventCardImpression}
            trackEventCardLocationClick={this.trackEventCardLocationClick}
            trackEventCardTitleClick={this.trackEventCardTitleClick}
          />
        </div>
      );
    }

    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }
}

BibPageContainer.propTypes = {
  analyticsContext: bibContextShape.isRequired,
  cardSearchParameters: ImmutablePropTypes.mapContains({
    requestId: PropTypes.string
  }),
  catalogBib: catalogBibShape,
  eventCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  linkedData: linkedDataShape.isRequired,
  promoCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  promosById: ImmutablePropTypes.mapOf(promoShape, PropTypes.string),
  router: routerShape.isRequired,

  // Props below are used to conditionally render section titles and dividers
  authorSuggestions: ImmutablePropTypes.listOf(authorSuggestionShape).isRequired,
  currentLibrary: libraryShape.isRequired,
  manifestations: ImmutablePropTypes.listOf(bibShape).isRequired,
  onlineResources: ImmutablePropTypes.listOf(onlineResourceShape).isRequired,
  recommendations: ImmutablePropTypes.listOf(recommendationShape).isRequired,
  relatedAuthors: ImmutablePropTypes.listOf(relatedAuthorShape).isRequired,
  relatedTitles: ImmutablePropTypes.listOf(catalogBibShape).isRequired,
  searchCards: PropTypes.func.isRequired,
  staffLists: ImmutablePropTypes.listOf(listShape).isRequired,
  tags: ImmutablePropTypes.listOf(tagShape).isRequired,
  trackCatalogExplore: PropTypes.func.isRequired,
  trackContentClick: PropTypes.func.isRequired,
  trackContentExplore: PropTypes.func.isRequired,
  trackContentImpression: PropTypes.func.isRequired
};

BibPageContainer.contextType = Context;

const mapStateToProps = state => ({
  cardSearchParameters: selectCardSearchParameters(state),
  catalogBib: selectCatalogBib(state),
  currentLibrary: selectCurrentLibrary(state),
  eventCards: selectEventCards(state),
  linkedData: selectLinkedDataForCurrentBib(state),
  promoCards: selectCardsForSearch(state),
  promosById: selectPromoEntities(state),
  analyticsContext: selectAnalyticsContextForCurrentBib(state),

  // Props below are used to conditionally render section titles and dividers
  manifestations: selectManifestationsForCurrentBib(state),
  staffLists: selectStaffListsForCurrentBib(state),
  relatedTitles: selectRelatedTitlesForCurrentBib(state),
  recommendations: selectRecommendationsForCurrentBib(state),
  tags: selectTagSummariesForCurrentBib(state),
  authorSuggestions: selectAuthorSuggestionsForCurrentBib(state),
  relatedAuthors: selectRelatedAuthorsForCurrentBib(state),
  onlineResources: selectOnlineResourcesForCurrentBib(state)
});

const mapDispatchToProps = {
  searchCards: searchCardsAction,
  trackCatalogExplore,
  trackContentClick: trackContentClickAction,
  trackContentExplore: trackContentExploreAction,
  trackContentImpression: trackContentImpressionAction
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(BibPageContainer)));
